import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Tab, Tabs } from "react-bootstrap";
import { useQuery } from "react-query";
import axios from "axios";

import ProofOfAddressConsole from "../../../screens/proofOfAddressConsole";
import VeriffSessionTable from "../VeriffSessionTable";
import {
  getProofOfAddress,
  approveProofOfAddress,
  updateSanctionsOutcome,
  getCardDetailByUserId,
  getAuditLog,
} from "../../../services/userServices";
import SanctionsOutcome from "../../../screens/sanctionsOutcome";
import styles from "./index.module.scss";
import HoldsTable from "../HoldsTable";
import PositionsTable from "../PositionsTable";
import AliasTable from "../AliasTable";
import CardTokensTable from "../CardTokensTable";
import EddaAccountsTable from "../EddaAccountsTable";
import StoredResponseTab from "../StoredResponseTab";
import { QueryKeyEnum } from "../../../types";
import { UserType } from "../../../types/user";

enum TabsKeyEnum {
  VERIFF_SESSIONS = "",
  PROOF_OF_ADDRESS = "ProofOfAddress",
  SANCTIONS_OUTCOME = "SanctionsOutcome",
  CKA = "CKA",
  AI = "AI",
  CRS = "CRS",
  HOLDS = "Holds",
  POSITIONS = "Positions",
  ALIAS = "Alias",
  ACTIVATE_CARD = "ACTIVATE_CARD",
  EDDA_ACCOUNTS = "EddaAccounts",
}

const getTabFromHash = (hash: string) =>
  hash.slice(1) || TabsKeyEnum.VERIFF_SESSIONS;

interface UserTabDetailProps {
  userData?: UserType;
}

const UserTabDetail = ({ userData }: UserTabDetailProps) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromHash(hash));

  const { data: cardDetail } = useQuery(
    [QueryKeyEnum.GetCardDetail, userId],
    () => getCardDetailByUserId(userId || ""),
    {
      enabled: !!userId,
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
    }
  );

  const hasCardDetail = !!cardDetail;

  useEffect(() => {
    setActiveTab(getTabFromHash(hash));
  }, [hash]);

  return (
    <Tabs
      activeKey={activeTab}
      id="fill-tab-example"
      className={styles.userTabDetail}
      fill
      onSelect={(activeKey) => {
        const key = activeKey ?? TabsKeyEnum.VERIFF_SESSIONS;
        setActiveTab(key);
        navigate(`#${key}`, { replace: true });
      }}
    >
      <Tab
        eventKey={TabsKeyEnum.VERIFF_SESSIONS}
        title="Veriff sessions"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <VeriffSessionTable />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.ALIAS}
        title="Alias"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <AliasTable />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.PROOF_OF_ADDRESS}
        title="Proof of address"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <ProofOfAddressConsole
          userData={userData}
          getProofOfAddress={getProofOfAddress}
          getAuditLog={getAuditLog}
          approve={approveProofOfAddress}
        />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.SANCTIONS_OUTCOME}
        title="Sanctions outcome"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <SanctionsOutcome
          userData={userData}
          getAuditLog={getAuditLog}
          updateSanctionsOutcome={updateSanctionsOutcome}
        />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.CKA}
        title="CKA"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <StoredResponseTab tableName="CKA" />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.AI}
        title="AI"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <StoredResponseTab tableName="AI" />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.CRS}
        title="CRS"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <StoredResponseTab tableName="CRS" />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.HOLDS}
        title="Holds"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <HoldsTable userData={userData} />
      </Tab>
      <Tab
        eventKey={TabsKeyEnum.POSITIONS}
        title="Positions"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <PositionsTable userData={userData} />
      </Tab>
      {hasCardDetail && (
        <Tab
          eventKey={TabsKeyEnum.ACTIVATE_CARD}
          title="Card tokens"
          className={styles.userTabDetailItem}
          mountOnEnter
          unmountOnExit
        >
          <CardTokensTable isActive={activeTab === TabsKeyEnum.ACTIVATE_CARD} />
        </Tab>
      )}
      <Tab
        eventKey={TabsKeyEnum.EDDA_ACCOUNTS}
        title="eDDA accounts"
        className={styles.userTabDetailItem}
        mountOnEnter
        unmountOnExit
      >
        <EddaAccountsTable />
      </Tab>
    </Tabs>
  );
};

export default UserTabDetail;
